import _ from 'lodash';
import { generatePath, matchPath } from 'react-router-dom';
import { createSelector } from '@reduxjs/toolkit';
import { locale } from 'src/i18n';
import type { RootState } from 'src/store';
import { getPlatformPrefixPathFromHash, normalizePath } from './queries';
import {
  getAppLocationInfo,
  getHostAppLocation,
  isLmsWeb,
  setAppRouteParams,
} from './utils';

export const getWorkspaceState = (state: RootState) => state?.workspace;

export const getWorkspaceModules = (state: RootState) =>
  getWorkspaceState(state)?.modulesList ?? [];

export const getWorkspaceRedirectPath = (state: RootState) =>
  getWorkspaceState(state).redirectPath;

export const getWorkspaceFullPage = (state: RootState) =>
  getWorkspaceState(state)?.fullPage;

export const getShowLeftNavigation = (state: RootState) =>
  getWorkspaceState(state).showLeftNavigation;

export const getWorkspaceDialogs = (state: RootState) =>
  getWorkspaceState(state)?.dialogs;

export const getWorkspaceRouteModule = (state: RootState) =>
  getWorkspaceState(state)?.routeModule;

export const getWorkspaceRouteModuleBreadcrumbs = createSelector(
  [getWorkspaceRouteModule],
  (currentModule) => {
    if (currentModule) {
      const { breadCrumbs = { items: [] }, path: currentModulePath } =
        currentModule;
      if (_.isEmpty(breadCrumbs?.items) || !currentModulePath) return [];

      const { hash } = getAppLocationInfo();
      const { params = {} } = matchPath(currentModulePath, hash) ?? {};
      const { pathParams: hostAppPathParams = {} } = getHostAppLocation() ?? {};

      const updatedParams = {
        ...params,
        ...hostAppPathParams,
        ...(currentModule.breadCrumbExtraParams ?? {}),
      };

      const { hidden } = updatedParams;

      return {
        design: breadCrumbs?.design,
        hidden,
        items: breadCrumbs.items
          .map(({ breadcrumbName, path, legacy }) => {
            if (!legacy && isLmsWeb()) {
              path = `${getPlatformPrefixPathFromHash()}${normalizePath(path)}`;
            }

            try {
              const localeKey = breadcrumbName?.localeKey
                ? locale(breadcrumbName.localeKey)
                : '';
              let routeParamKey = breadcrumbName?.routeParamKey;
              if (!_.isEmpty(params)) {
                path = generatePath(String(path), updatedParams);
                if (routeParamKey) {
                  routeParamKey = updatedParams[routeParamKey];
                }
              }

              return {
                breadcrumbName: {
                  ...breadcrumbName,
                  routeParamKey,
                  localeKey,
                },
                path,
                legacy,
              };
            } catch (e) {
              return null;
            }
          })
          .filter(Boolean),
      };
    }
    return [];
  },
);

export const syncAppRouteHash = createSelector(
  [
    getWorkspaceRouteModule,
    (state, params: Object, replace = true) => ({ params, replace }),
  ],
  (currentModule, { params, replace }) => {
    if (currentModule) {
      let routeHash = currentModule?.path;
      Object.entries(params).forEach(([key, value]) => {
        if (routeHash) {
          routeHash = routeHash.replace(`:${key}`, value);
        }
      });

      if (routeHash) {
        setAppRouteParams(replace, routeHash);
      }
    }
  },
);

export const getPropsForCurrentModuleByUrl = createSelector(
  [getWorkspaceRouteModule, (state, url) => url],
  (currentModule, url) => {
    if (currentModule) {
      const { path: currentModulePath } = currentModule;
      const modulePropsData = matchPath(
        normalizePath(currentModulePath),
        getAppLocationInfo(url).hash,
      );
      return modulePropsData?.params ?? {};
    }
    return {};
  },
);
